'use client';

import Image from '@/components/Image';
import MagicJump from '@/components/MagicJump';
import { env } from '@/utils/env';
import { useCallback, useRef, useState } from 'react';
import type { Swiper as SwiperType } from 'swiper';
import { Autoplay, Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import type { BannerData } from '../types';
import styles from './CampaignBanner.module.css';

interface CampaignBannerProps {
  data: BannerData;
  aspectRatio?: string;
  className?: string;
  contentClassName?: string;
  imageClassName?: string;
  width?: number;
  height?: number;
  sizes?: string;
  bannerIndex?: number;
  onBannerClick?: (data: BannerData, index: number) => void;
  onImageLoad?: (image: HTMLImageElement) => void;
}

interface CampaignBannerCarouselProps extends Omit<CampaignBannerProps, 'data'> {
  data: BannerData[];
  autoplayDelay?: number;
  isMobile?: boolean;
  pauseOnMouseEnter?: boolean;
  onActiveIndexChange?: (index: number) => void;
}

function resolveAssetUrl(path?: string): string {
  if (!path) return '';
  if (/^(https?:)?\/\//.test(path)) return path;
  return `${env.NEXT_PUBLIC_S3_URL}${path}`;
}

export default function CampaignBanner({
  data,
  aspectRatio,
  className = 'w-full',
  contentClassName = 'relative block h-full w-full',
  imageClassName = 'block h-auto w-full object-cover object-center',
  width = 1920,
  height = 1080,
  sizes,
  bannerIndex = 0,
  onBannerClick,
  onImageLoad,
}: CampaignBannerProps) {
  if (!data?.image) {
    return null;
  }

  const alt = data.image_alt || 'activity banner';
  /* eslint-disable jsx-a11y/no-noninteractive-element-interactions -- 图片加载只用于同步 Swiper 的活动 slide 高度。 */
  const content = (
    <div className={contentClassName}>
      <Image
        src={resolveAssetUrl(data.image)}
        alt={alt}
        width={width}
        height={height}
        sizes={sizes}
        className={imageClassName}
        priority={bannerIndex === 0}
        onLoad={(event) => onImageLoad?.(event.currentTarget)}
      />
    </div>
  );
  /* eslint-enable jsx-a11y/no-noninteractive-element-interactions */

  return (
    <div className={className} style={aspectRatio ? { aspectRatio } : undefined}>
      {data.jump_content?.Jump_target ? (
        <MagicJump
          item={data.jump_content}
          className="block h-full w-full cursor-pointer"
          aria-label={alt}
          onJumpClick={() => onBannerClick?.(data, bannerIndex)}
        >
          {content}
        </MagicJump>
      ) : (
        content
      )}
    </div>
  );
}

export function CampaignBannerCarousel({
  data,
  aspectRatio,
  className = 'w-full',
  contentClassName = 'relative block h-full w-full',
  imageClassName = 'block h-auto w-full object-cover object-center',
  width = 1920,
  height = 1080,
  sizes,
  autoplayDelay = 5000,
  isMobile = false,
  pauseOnMouseEnter = true,
  onActiveIndexChange,
  onBannerClick,
}: CampaignBannerCarouselProps) {
  const banners = data.filter((item) => item?.image);
  const swiperRef = useRef<SwiperType | null>(null);
  const [firstBannerAspectRatio, setFirstBannerAspectRatio] = useState<number | null>(null);
  const sharedBannerHeight = firstBannerAspectRatio
    ? Math.round(width / firstBannerAspectRatio)
    : height;
  const sharedBannerAspectRatio = firstBannerAspectRatio
    ? `${width} / ${sharedBannerHeight}`
    : undefined;
  const carouselAspectRatio = aspectRatio || sharedBannerAspectRatio;

  const updateAutoHeight = useCallback(() => {
    requestAnimationFrame(() => {
      const swiper = swiperRef.current;

      if (!swiper || swiper.destroyed) {
        return;
      }

      swiper.updateAutoHeight(0);
    });
  }, []);

  const handleSwiper = useCallback(
    (swiper: SwiperType) => {
      swiperRef.current = swiper;
      onActiveIndexChange?.(swiper.realIndex || 0);
      updateAutoHeight();
    },
    [onActiveIndexChange, updateAutoHeight],
  );

  const handleSlideChange = useCallback(
    (swiper: SwiperType) => {
      onActiveIndexChange?.(swiper.realIndex || 0);
      updateAutoHeight();
    },
    [onActiveIndexChange, updateAutoHeight],
  );

  const handleFirstBannerImageLoad = useCallback(
    (image: HTMLImageElement) => {
      const { naturalHeight, naturalWidth } = image;

      if (naturalWidth > 0 && naturalHeight > 0) {
        const nextAspectRatio = naturalWidth / naturalHeight;

        setFirstBannerAspectRatio((current) =>
          current === nextAspectRatio ? current : nextAspectRatio,
        );
      }

      updateAutoHeight();
    },
    [updateAutoHeight],
  );

  if (banners.length === 0) {
    return null;
  }

  if (banners.length === 1) {
    return (
      <CampaignBanner
        data={banners[0]}
        aspectRatio={carouselAspectRatio || banners[0].aspectRatio}
        className={className}
        contentClassName={contentClassName}
        imageClassName={imageClassName}
        width={width}
        height={height}
        sizes={sizes}
        bannerIndex={0}
        onBannerClick={onBannerClick}
      />
    );
  }

  return (
    <Swiper
      className={`${className} ${styles.campaignBanner} ${isMobile ? styles.mobile : styles.desktop}`}
      style={carouselAspectRatio ? { aspectRatio: carouselAspectRatio } : undefined}
      modules={[Autoplay, Pagination]}
      loop
      autoplay={{
        delay: autoplayDelay,
        disableOnInteraction: false,
        pauseOnMouseEnter,
      }}
      autoHeight
      pagination={{ clickable: true }}
      observer
      observeParents
      observeSlideChildren
      onSwiper={handleSwiper}
      onSlideChange={handleSlideChange}
    >
      {banners.map((banner, index) => (
        <SwiperSlide key={`${banner.image}-${index}`}>
          <CampaignBanner
            data={banner}
            aspectRatio={banner.aspectRatio || carouselAspectRatio}
            className="h-full w-full"
            contentClassName={contentClassName}
            imageClassName={imageClassName}
            width={width}
            height={sharedBannerHeight}
            sizes={sizes}
            bannerIndex={index}
            onBannerClick={onBannerClick}
            onImageLoad={index === 0 ? handleFirstBannerImageLoad : updateAutoHeight}
          />
        </SwiperSlide>
      ))}
    </Swiper>
  );
}
